import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, GraduationCap, BedDouble } from "lucide-react";

const TABS = [
  { key: "academic", label: "Academy Admission", icon: GraduationCap },
  { key: "hostel",   label: "Hostel Admission",  icon: BedDouble },
];

export default function AdminAdmission() {
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("academic");
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/admission"); setContent(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async (section, data) => {
    setSaving(true);
    try {
      const { data: fresh } = await api.post("/admin/admission", { section, data });
      setContent(fresh);
      toast.success("Section saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!content) return <div className="text-white/60">Loading…</div>;

  return (
    <div data-testid="admin-admission">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Content · Admission Procedure</div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Admission Procedure.</h1>
        <a href="/admission" target="_blank" rel="noreferrer" data-testid="admission-view-live"
          className="border border-brand-orange text-brand-orange px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-brand-orange hover:text-black">
          View Live Page ↗
        </a>
      </div>

      <div className="flex items-center gap-1 border-b border-white/12 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admission-tab-${t.key}`}
              className={`flex items-center gap-2 px-5 py-3 text-xs font-mono uppercase tracking-widest ${tab === t.key ? "border-b-2 border-brand-orange brand-orange" : "text-white/50 hover:text-white"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      <SectionEditor
        data={content[tab]}
        onSave={(d) => save(tab, d)}
        saving={saving}
        key={tab}
      />
    </div>
  );
}

function SectionEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data);
  useEffect(() => setForm(data), [data]);
  const fees = form.fees || [];
  const docs = form.documents || [];
  const terms = form.terms || [];
  const setKey = (k, v) => setForm({ ...form, [k]: v });

  const addFee = () => setKey("fees", [...fees, { label: "", amount: 0, refundable: false, description: "" }]);
  const updateFee = (i, patch) => setKey("fees", fees.map((x, j) => j === i ? { ...x, ...patch } : x));
  const rmFee = (i) => setKey("fees", fees.filter((_, j) => j !== i));

  const totalNonRefund = fees.filter((f) => !f.refundable).reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);
  const totalRefund = fees.filter((f) => f.refundable).reduce((a, f) => a + (parseFloat(f.amount) || 0), 0);

  return (
    <div>
      {/* Meta */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Field label="Title"><input value={form.title || ""} onChange={(e) => setKey("title", e.target.value)} data-testid="adm-title" className="w-full bg-transparent border border-white/20 py-3 px-4" /></Field>
        <Field label="Subtitle"><input value={form.subtitle || ""} onChange={(e) => setKey("subtitle", e.target.value)} data-testid="adm-subtitle" className="w-full bg-transparent border border-white/20 py-3 px-4" /></Field>
      </div>
      <Field label="Hero Image URL"><input value={form.hero_image || ""} onChange={(e) => setKey("hero_image", e.target.value)} data-testid="adm-hero" className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm mb-4" /></Field>
      <Field label="Intro Paragraph"><textarea rows={3} value={form.intro || ""} onChange={(e) => setKey("intro", e.target.value)} data-testid="adm-intro" className="w-full bg-transparent border border-white/20 py-3 px-4 text-sm mb-6" /></Field>

      {/* Fees */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg uppercase font-bold brand-orange">Fees & Charges</div>
          <div className="text-xs text-white/60 font-mono">
            Non-refundable: <span className="text-white">₹{totalNonRefund.toLocaleString("en-IN")}</span> ·
            Refundable: <span className="brand-blue">₹{totalRefund.toLocaleString("en-IN")}</span>
          </div>
        </div>
        <div className="space-y-2">
          {fees.map((f, i) => (
            <div key={i} className="bg-[#131A2B] border border-white/12 p-3 flex items-start gap-2 flex-wrap md:flex-nowrap" data-testid={`fee-row-${i}`}>
              <input value={f.label || ""} onChange={(e) => updateFee(i, { label: e.target.value })} placeholder="Fee label" className="flex-1 min-w-[140px] bg-transparent border border-white/20 py-2 px-3 text-sm" />
              <input type="number" value={f.amount ?? 0} onChange={(e) => updateFee(i, { amount: parseFloat(e.target.value) || 0 })} placeholder="₹" className="w-28 bg-transparent border border-white/20 py-2 px-3 text-sm font-mono" />
              <input value={f.package || ""} onChange={(e) => updateFee(i, { package: e.target.value })} placeholder="Package/variant (e.g. With Education)" className="w-56 bg-transparent border border-white/20 py-2 px-3 text-xs" />
              <label className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-mono cursor-pointer border border-white/20">
                <input type="checkbox" checked={!!f.refundable} onChange={(e) => updateFee(i, { refundable: e.target.checked })} />
                Refundable
              </label>
              <input value={f.description || ""} onChange={(e) => updateFee(i, { description: e.target.value })} placeholder="Description" className="flex-1 min-w-[200px] bg-transparent border border-white/20 py-2 px-3 text-sm" />
              <button onClick={() => rmFee(i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={addFee} data-testid="add-fee" className="w-full border border-dashed border-white/20 py-3 text-white/60 hover:text-white text-sm font-mono uppercase tracking-widest flex items-center justify-center gap-2">
            <Plus size={14} /> Add Fee Line
          </button>
        </div>
      </div>

      <Field label="Wallet / Imprest Note (shown on hostel section, describes the ward-wallet system)">
        <textarea rows={3} value={form.wallet_note || ""} onChange={(e) => setKey("wallet_note", e.target.value)}
          data-testid="adm-wallet-note" placeholder="E.g. 'An imprest amount of ₹25,000 is required each quarter…'"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-sm mb-6" />
      </Field>

      {/* Documents */}
      <StringListEditor label="Documents Required" items={docs} setItems={(v) => setKey("documents", v)} testid="documents" />

      {/* Terms */}
      <StringListEditor label="Terms & Conditions" items={terms} setItems={(v) => setKey("terms", v)} testid="terms" />

      <div className="sticky bottom-4 flex items-center justify-between bg-brand-blue text-white px-5 py-3 mt-8 shadow-2xl">
        <div className="text-xs font-mono uppercase tracking-widest">Ready to save</div>
        <button onClick={() => onSave(form)} disabled={saving} data-testid="admission-save"
          className="bg-black text-white px-6 py-2 text-xs font-bold uppercase tracking-widest hover:bg-white/10 flex items-center gap-2 disabled:opacity-50">
          <Save size={13} /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function StringListEditor({ label, items, setItems, testid }) {
  return (
    <div className="mb-8" data-testid={`section-${testid}`}>
      <div className="font-display text-lg uppercase font-bold brand-blue mb-3">{label}</div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <span className="pt-2 text-white/40">•</span>
            <input value={v} onChange={(e) => setItems(items.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 bg-transparent border border-white/20 py-2 px-3 text-sm" />
            <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-2"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => setItems([...items, ""])} data-testid={`add-${testid}`} className="w-full border border-dashed border-white/15 py-2 text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white flex items-center justify-center gap-1">
          <Plus size={12} /> Add item
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
    </div>
  );
}
